import { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import DcsdDialog from "../modals/DcsdDialog";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import Icon from "../icon/Icon";
import LoadingSvg from "../loadingSvg/LoadingSvg";
import MoveButton from "../formInputs/buttons/MoveButton";
import UserDao from "../../dao/UserDao";

import { getTodayWithTime } from "../../utils/DateFormatter";
import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/SchoolHome.scss";
import "../../styles/SchoolLevelExpectations.scss";

const SchoolLevelExpectations = ({ fullYearTimePeriodKey, locationKey }) => {
    const { state } = useContext(GlobalContext);
    const { schoolYearDto, token, userDetails } = state;

    const [deleteAllMode, setDeleteAllMode] = useState(false);
    const [existingRatingInfo, setExistingRatingInfo] = useState([]);
    const [historicalSles, setHistoricalSles] = useState([]);
    const [loader, setLoader] = useState(false);
    const [open, setOpen] = useState("false");
    const [schoolYears, setSchoolYears] = useState(null);
    const [selectedYear, setSelectedYear] = useState("");
    const [sles, setSles] = useState();
    const [sleChildToDelete, setSleChildToDelete] = useState(null);
    const [sleToDelete, setSleToDelete] = useState(null);
    const [touched, setTouched] = useState(false);

    const [autoSaveMessage, setAutoSaveMessage] = useState("");

    const handleSelectChange = (e) => {
        setSelectedYear(e.target.value);
        if (e.target.value !== "") {
            const options = {
                action: "getSchoolLevelExpectations",
                locationKey: locationKey,
                schoolYearKey: schoolYears.find((year) => year.name === e.target.value).key,
                token
            };
            setLoader(true);
            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response && response.data && response.data.payload) {
                        const historicalSles = response.data.payload;
                        historicalSles.forEach((item, idx) => {
                            if (item.ordinal === null) {
                                item.ordinal = idx;
                            }
                        });
                        setHistoricalSles(historicalSles);
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        } else {
            setHistoricalSles([]);
        }
    };

    const handleCopySles = () => {
        const selectedYearObj = schoolYears.find((year) => year.name === selectedYear);
        const options = {
            action: "copySchoolLevelExpectations",
            locationKey: locationKey,
            data: {
                fromSchoolYearKey: selectedYearObj.key,
                toSchoolYearKey: schoolYearDto.key,
                lastUpdaterGuid: userDetails.uid
            },
            token
        };
        setLoader(true);
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response && response.data && response.data.payload) {
                    toast.success(`School Level Expectations successfully copied and saved.`, {
                        autoClose: 3000
                    });
                    setSles(response.data.payload);
                }
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    const handleAddSle = () => {
        const newItem = {
            key: null,
            locationKey: locationKey,
            schoolYearKey: schoolYearDto.key,
            timePeriod: fullYearTimePeriodKey,
            highLight: false,
            ordinal: sles.length,
            lastUpdaterGuid: userDetails.uid,
            expectation: "",
            sleChildList: []
        };

        setSles([...sles, newItem]);
    };

    const handleAddChild = (index) => {
        const newChild = {
            key: null,
            ordinal: sles[index].sleChildList.length,
            expectation: ""
        };

        const updatedSles = [...sles];
        updatedSles[index].sleChildList.push(newChild);
        setSles(updatedSles);
    };

    const handleEdit = (index, value, childIndex = null) => {
        setTouched(true);
        const updatedSles = [...sles];
        if (childIndex === null) {
            updatedSles[index].expectation = value;
        } else {
            updatedSles[index].sleChildList[childIndex].expectation = value;
        }
        setSles(updatedSles);
    };

    const moveItem = (list, parentIndex, childIndex = null, direction) => {
        setTouched(true);
        const newList = [...list];

        if (childIndex === null) {
            const [item] = newList.splice(parentIndex, 1);
            newList.splice(parentIndex + direction, 0, item);
        } else {
            const childList = [...newList[parentIndex].sleChildList];
            const [child] = childList.splice(childIndex, 1);
            childList.splice(childIndex + direction, 0, child);
            newList[parentIndex].sleChildList = childList;
        }

        // Recalculate ordinals
        return newList.map((item, idx) => ({
            ...item,
            ordinal: idx,
            sleChildList: item.sleChildList.map((child, cIdx) => ({
                ...child,
                ordinal: cIdx
            }))
        }));
    };

    const handleSave = useCallback(
        (sleData, autoSave = false) => {
            if (!schoolYearDto) return;
            const options = {
                action: "updateSchoolLevelExpectations",
                locationKey: locationKey,
                schoolYearKey: schoolYearDto?.key,
                params: { lastUpdaterGuid: userDetails.uid },
                data: sleData,
                token
            };

            if (!autoSave) {
                setLoader(true);
            }

            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response && response.data && response.data.payload) {
                        if (!autoSave) {
                            toast.success("School Level Expectations successfully saved.", { autoClose: 3000 });
                        } else {
                            setAutoSaveMessage(`Saved: ${getTodayWithTime()}`);
                            setTimeout(() => setAutoSaveMessage(""), 3000);
                        }
                        setSles(response.data.payload);
                    }

                    if (!autoSave) {
                        setLoader(false);
                    }
                    setTouched(false);
                })
                .catch((error) => {
                    console.error(error);
                });
        },
        [locationKey, schoolYearDto, userDetails.uid, token]
    );

    const handleDeleteSle = (index, sleKey, isFirstTime) => {
        const updatedSles = [...sles];
        updatedSles.splice(index, 1);

        updatedSles.forEach((item, idx) => {
            item.ordinal = idx;
        });
        if (!sleKey) {
            setSles(updatedSles);
        }

        if (sleKey) {
            const options = {
                action: "deleteSchoolLevelExpectations",
                params: {
                    idList: sleKey,
                    isFirstTime
                },
                token
            };
            setLoader(true);
            ElementaryProgressReportDao(options)
                .then((response) => {
                    const payload = response?.data?.payload ?? [];
                    if (!response?.errors && Array.isArray(payload) && payload.length === 0) {
                        toast.success(`School Level Expectation successfully removed.`, {
                            autoClose: 3000
                        });
                        handleSave(updatedSles);
                        return;
                    }
                    if (!response?.errors && Array.isArray(payload) && payload.length > 0) {
                        setExistingRatingInfo(payload);
                        setSleToDelete({ index, sleKey });
                        setTimeout(() => setOpen("confirm-remove"), 0);

                        return;
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    const handleDeleteChild = (index, childIndex, childKey, isFirstTime) => {
        const updatedSles = sles.map((sle, i) => {
            if (i !== index) return sle;

            const newChildList = sle.sleChildList
                .filter((_, cIdx) => cIdx !== childIndex)
                .map((child, idx) => ({
                    ...child,
                    ordinal: idx
                }));

            return {
                ...sle,
                sleChildList: newChildList
            };
        });
        if (!childKey) {
            setSles(updatedSles);
            return;
        }
        if (childKey) {
            const options = {
                action: "deleteSchoolLevelExpectationChildren",
                params: {
                    idList: childKey,
                    isFirstTime
                },
                token
            };
            setLoader(true);
            ElementaryProgressReportDao(options)
                .then((response) => {
                    const payload = response?.data?.payload ?? [];
                    if (!response?.errors && Array.isArray(payload) && payload.length === 0) {
                        toast.success(`Sub-school Level Expectation successfully removed.`, {
                            autoClose: 3000
                        });
                        handleSave(updatedSles);
                        setSles(updatedSles);

                        return;
                    }
                    if (!response?.errors && Array.isArray(payload) && payload.length > 0) {
                        setExistingRatingInfo(payload);
                        setSleChildToDelete({ index, childIndex, childKey });
                        setTimeout(() => setOpen("confirm-remove"), 0);
                        return;
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    const handleDeleteAll = (isFirstTime) => {
        const options = {
            action: "deleteAllSchoolLevelExpectations",
            locationKey: locationKey,
            schoolYearKey: schoolYearDto.key,
            params: {
                isFirstTime
            },
            token
        };
        setLoader(true);
        ElementaryProgressReportDao(options)
            .then((response) => {
                const payload = response?.data?.payload ?? [];

                if (response && !response.errors) {
                    if (Array.isArray(payload) && payload.length > 0) {
                        setExistingRatingInfo(payload);
                        setDeleteAllMode(true);
                        setOpen("confirm-remove");
                    } else {
                        toast.success(`School Level Expectations successfully deleted.`, {
                            autoClose: 3000
                        });
                        setSles([]);
                        setOpen("false");
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    /**
     * Get SLEs if they exist
     */
    useEffect(() => {
        if (schoolYearDto?.key && locationKey && token) {
            const options = {
                action: "getSchoolLevelExpectations",
                locationKey: locationKey,
                schoolYearKey: schoolYearDto.key,
                token
            };
            setLoader(true);
            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response && response.data && response.data.payload) {
                        setSles(response.data.payload);
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [locationKey, schoolYearDto, token]);

    /**
     * Get school years
     */
    useEffect(() => {
        if (!schoolYearDto) return;
        if (!schoolYears && token) {
            const options = {
                action: "schoolYearsRead",
                token
            };
            setLoader(true);
            UserDao(options)
                .then((response) => {
                    if (response && response.data && response.data.payload) {
                        const prevFiveYears = response.data.payload
                            .filter((year) => year.key < schoolYearDto.key)
                            .slice(-5);
                        setSchoolYears(prevFiveYears.reverse());
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [schoolYearDto, schoolYears, token]);

    useEffect(() => {
        if (touched) {
            const autoSaveTimer = setTimeout(() => handleSave(sles, true), 2000);
            return () => clearTimeout(autoSaveTimer); // Cancel if user keeps typing
        }
    }, [handleSave, sles, touched]);

    const getDeleteTargetDescription = () => {
        if (deleteAllMode) {
            return "all School Level Expectations and any applicable Sub-school Level Expectations for this school year.";
        }

        if (sleToDelete) {
            return "this School Level Expectation and any applicable Sub-school Level Expectations associated with it.";
        }

        return "this Sub-school Level Expectation.";
    };

    const renderAffectedTeachers = () => {
        if (!existingRatingInfo || existingRatingInfo.length === 0) return null;

        const getTotalRatings = (ratingCounts) =>
            ratingCounts.reduce((sum, rc) => {
                const parent = rc.ratingCount || 0;
                const children = (rc.childRatingCounts || []).reduce(
                    (childSum, child) => childSum + (child.ratingCount || 0),
                    0
                );
                return sum + parent + children;
            }, 0);

        return (
            <ul className="affected-teachers-list">
                {existingRatingInfo.map(({ teacherGuid, teacherName, ratingCounts }) => {
                    const totalRatings = getTotalRatings(ratingCounts);

                    return (
                        <li key={teacherGuid} style={{ marginBottom: "8px" }}>
                            <div>
                                <b>{teacherName}</b> has{" "}
                                <b>
                                    {totalRatings} rating
                                    {totalRatings !== 1 ? "s." : "."}
                                </b>
                            </div>

                            <ul className="affected-teacher-expectations-list">
                                {ratingCounts.map((rc) => (
                                    <li key={`${teacherGuid}-${rc.id}`} style={{ marginTop: "4px" }}>
                                        <div>
                                            <u>{rc.expectation}</u>{" "}
                                            {(sleToDelete || deleteAllMode) && (
                                                <b style={{ fontSize: ".8rem" }}>
                                                    ({rc.ratingCount} rating
                                                    {rc.ratingCount !== 1 ? "s" : ""})
                                                </b>
                                            )}
                                        </div>

                                        {rc.childRatingCounts?.length > 0 && (
                                            <ul>
                                                {rc.childRatingCounts.map((child) => (
                                                    <li key={`${teacherGuid}-${child.id}`}>
                                                        <u>{child.expectation}</u>{" "}
                                                        <b style={{ fontSize: ".8rem" }}>
                                                            ({child.ratingCount} rating
                                                            {child.ratingCount !== 1 ? "s" : ""})
                                                        </b>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const cleanUpSleState = () => {
        setOpen("false");
        setExistingRatingInfo([]);
        setSleToDelete(null);
        setSleChildToDelete(null);
        setDeleteAllMode(false);
    };
    const handleConfirmDeleteClick = () => {
        if (deleteAllMode) {
            handleDeleteAll(false);
        } else if (sleToDelete) {
            handleDeleteSle(sleToDelete.index, sleToDelete.sleKey, false);
        } else if (sleChildToDelete) {
            handleDeleteChild(sleChildToDelete.index, sleChildToDelete.childIndex, sleChildToDelete.childKey, false);
        }

        cleanUpSleState();
    };

    if (!schoolYearDto) return;

    return (
        schoolYearDto && (
            <div className="template-wrapper">
                <div className="template-title-section">
                    <h4 className="page-title" style={{ marginBottom: "0px" }}>
                        Setup School Level Expectations for the {schoolYearDto?.name} School Year
                    </h4>
                </div>
                <section>
                    {sles?.length === 0 && (
                        <div className="sles-helper-text">
                            You may select School Level Expectations from a previous school year to copy forward and use
                            this year. If you elect to copy, you will have the opportunity to edit the expectations, add
                            new expectations and re-order the expectations. Alternatively, you may click
                            &quot;Begin&quot; to create new SLEs.
                        </div>
                    )}
                    {sles?.length > 0 && (
                        <div className="sles-helper-text">
                            Please review and edit School Level Expectations. If you want to add a new SLE, please click
                            &quot;Add SLE&quot; button. You may re-order expectations and sub-expectations by clicking
                            the up or down arrow to the left. Once you are satisfied, please click the &quot;Save&quot;
                            button.
                        </div>
                    )}
                    {sles && sles.length === 0 && (
                        <div className="copy-or-create-container">
                            <div className={historicalSles.length > 0 ? "card extra-width" : "card"}>
                                <div className="card-body">
                                    <p className="card-title">Copy from:</p>
                                    <div style={{ display: "flex" }}>
                                        <label htmlFor="sleYearSelect" className="visually-hidden">
                                            Copy from previous year:
                                        </label>
                                        <select
                                            className="form-select"
                                            id="sleYearSelect"
                                            name="sle-year-select"
                                            value={selectedYear}
                                            onChange={(e) => handleSelectChange(e)}
                                        >
                                            <option value="">Select school year</option>
                                            {schoolYears?.map((year, index) => {
                                                const { key, name } = year;
                                                const uniqueKey = `area-${key}-${index}`;
                                                return (
                                                    <option key={uniqueKey} value={name}>
                                                        {name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <div style={{ marginRight: "-10px" }}>
                                            <ActionButton
                                                className="action-button-reg"
                                                label="Clear"
                                                onClick={() => {
                                                    setSelectedYear("");
                                                    setHistoricalSles([]);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {historicalSles && historicalSles.length > 0 && (
                                        <>
                                            <div className="preview">
                                                <h4>Previewing {selectedYear} SLEs:</h4>
                                                <hr />
                                                <ol>
                                                    {historicalSles?.map((sle) => (
                                                        <li key={sle.key}>
                                                            {sle.expectation}
                                                            <ol>
                                                                {sle.sleChildList?.map((child) => (
                                                                    <li key={child.key}>{child.expectation}</li>
                                                                ))}
                                                            </ol>
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                            <div className="copy-btn-container">
                                                <ActionButton
                                                    className="action-button-reg"
                                                    label={`Copy ${selectedYear} SLEs`}
                                                    onClick={handleCopySles}
                                                />
                                            </div>
                                        </>
                                    )}
                                    {selectedYear && historicalSles.length === 0 && (
                                        <div className="preview">No SLEs available for selected year.</div>
                                    )}
                                </div>
                            </div>
                            <div className="separator">&ndash; OR &ndash;</div>
                            <div className={historicalSles.length > 0 ? "card less-width" : "card"}>
                                <div className="card-body">
                                    <p className="card-title">Create New SLEs</p>
                                    <ActionButton className="action-button-reg" label="Begin" onClick={handleAddSle} />
                                </div>
                            </div>
                        </div>
                    )}
                    {sles && sles.length > 0 && (
                        <div className="sles-container">
                            <div className={autoSaveMessage ? "auto-save-bar" : "auto-save-bar-hidden"}>
                                {autoSaveMessage}
                            </div>
                            <div className="sle-button-wrapper">
                                <div>
                                    <ActionButton
                                        className="action-button-reg"
                                        disabled={loader}
                                        onClick={handleAddSle}
                                        label={"Add SLE"}
                                    />
                                    <ActionButton
                                        className="action-button-reg"
                                        disabled={loader}
                                        onClick={() => handleSave(sles)}
                                        label="Save"
                                    />
                                    <ActionButton
                                        className="action-button-delete"
                                        disabled={loader}
                                        onClick={() => setOpen("delete-all")}
                                        label="Delete All SLEs"
                                    />
                                </div>
                            </div>
                            {!loader &&
                                sles?.map((item, index) => (
                                    <div className="sle-and-children" key={`${item.key}-${item.ordinal}`}>
                                        <div className="sle-wrapper">
                                            <div className="ordinal-movement-wrapper">
                                                <MoveButton
                                                    ariaLabel={`Move School Level Expectation ${index + 1} up`}
                                                    direction="up"
                                                    disabled={index === 0}
                                                    onClick={() => setSles(moveItem(sles, index, null, -1))}
                                                />
                                                <label htmlFor={`sle-textarea-${index}`}>{item.ordinal + 1}</label>
                                                <MoveButton
                                                    ariaLabel={`Move School Level Expectation ${index + 1} down`}
                                                    direction="down"
                                                    disabled={index === sles.length - 1}
                                                    onClick={() => setSles(moveItem(sles, index, null, 1))}
                                                />
                                            </div>

                                            <textarea
                                                aria-labelledby={`sle-textarea-${index}`}
                                                className="sle-text-area"
                                                id={`sle-textarea-${index}`}
                                                maxLength={3000}
                                                type="text"
                                                value={item.expectation}
                                                onChange={(e) => handleEdit(index, e.target.value)}
                                            />
                                            <div className="icon-btn-wrapper">
                                                <button
                                                    aria-label="Add sub-expectation"
                                                    className="button-icon-right"
                                                    disabled={loader}
                                                    onClick={() => handleAddChild(index)}
                                                >
                                                    <Icon fill="#FFF" iconName="PLUS_ICON" />
                                                </button>
                                                <button
                                                    aria-label="Delete SLE"
                                                    className="button-icon-right"
                                                    disabled={loader}
                                                    onClick={() => handleDeleteSle(index, item.key, true)}
                                                >
                                                    <Icon fill="#FFF" iconName="TRASHCAN" />
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            {item.sleChildList.map((child, childIndex) => (
                                                <div className="sle-child-wrapper" key={child.ordinal}>
                                                    <div className="ordinal-movement-wrapper">
                                                        <MoveButton
                                                            ariaLabel={`Move sub-expectation ${childIndex + 1} up`}
                                                            direction="up"
                                                            disabled={childIndex === 0}
                                                            onClick={() =>
                                                                setSles(moveItem(sles, index, childIndex, -1))
                                                            }
                                                        />
                                                        <label htmlFor={`sub-sle-textarea-${childIndex}`}>
                                                            {item.ordinal + 1}.{child.ordinal + 1}
                                                        </label>
                                                        <MoveButton
                                                            ariaLabel={`Move sub-expectation ${childIndex + 1} down`}
                                                            direction="down"
                                                            disabled={
                                                                childIndex === sles[index].sleChildList.length - 1
                                                            }
                                                            onClick={() =>
                                                                setSles(moveItem(sles, index, childIndex, 1))
                                                            }
                                                        />
                                                    </div>
                                                    <textarea
                                                        aria-labelledby={`sub-sle-textarea-${childIndex}`}
                                                        className="sle-child-text-area"
                                                        id={`sub-sle-textarea-${childIndex}`}
                                                        maxLength={3000}
                                                        type="text"
                                                        value={child.expectation}
                                                        onChange={(e) => handleEdit(index, e.target.value, childIndex)}
                                                    />
                                                    <div className="icon-btn-wrapper">
                                                        <button
                                                            aria-label="Delete sub-expectation"
                                                            className="button-icon"
                                                            disabled={loader}
                                                            onClick={() =>
                                                                handleDeleteChild(index, childIndex, child.key, true)
                                                            }
                                                        >
                                                            <Icon fill="#FFF" iconName="TRASHCAN" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </section>
                <DcsdDialog
                    actions={
                        <div className="modal-btn-group">
                            <ActionButton
                                className="action-button-cancel"
                                label="Cancel"
                                onClick={() => setOpen("false")}
                            />
                            <ActionButton
                                className="action-button-delete"
                                label="Confirm Delete"
                                onClick={() => {
                                    setOpen("false");
                                    handleDeleteAll(true);
                                }}
                            />
                        </div>
                    }
                    ariaLabel="Delete Confirmation Dialog"
                    hasCloseX={false}
                    id="delete-all"
                    open={open}
                    title="Delete SLEs Confirmation"
                >
                    <div>
                        Please confirm you would like to delete all School Level Expectations for the{" "}
                        {schoolYearDto.name} year.
                    </div>
                </DcsdDialog>
                <DcsdDialog
                    actions={
                        <div className="modal-btn-group">
                            <ActionButton
                                className="action-button-cancel"
                                label="Cancel"
                                onClick={() => {
                                    setOpen("false");
                                    cleanUpSleState();
                                }}
                            />
                            <ActionButton
                                className="action-button-reg"
                                label="Confirm and Delete"
                                onClick={handleConfirmDeleteClick}
                            />
                        </div>
                    }
                    ariaLabel="Confirmation Remove SLE Dialog"
                    hasCloseX={false}
                    id="confirm-remove"
                    open={open}
                    title={<span className="remove-dialog-title">Delete SLE Confirmation</span>}
                >
                    <div>
                        <div className="attention-required-title">⚠️ ATTENTION REQUIRED</div>

                        <div className="attention-required-text">
                            You are attempting to remove {getDeleteTargetDescription()} Doing so will delete the ratings
                            shown below.
                        </div>

                        <div className="affected-teachers-title">Affected Teachers:</div>

                        {renderAffectedTeachers()}

                        <div className="remove-dialog-imp-wrapper">
                            <span className="remove-dialog-imp-text">
                                Please confirm that you wish to proceed and acknowledge that you have notified affected
                                teachers. If you do not wish to proceed with these changes, click Cancel to return to
                                School Level Expectations.
                            </span>
                        </div>
                    </div>
                </DcsdDialog>
                {loader && <LoadingSvg />}
            </div>
        )
    );
};

export default SchoolLevelExpectations;

SchoolLevelExpectations.propTypes = {
    fullYearTimePeriodKey: PropTypes.string,
    locationKey: PropTypes.string
};
